import { VpButton } from '@vtmn-play/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductNaturesSearchInput } from '../select/productNature';

import { SelectOptions } from '@spo/logic';
import { Modal } from '@spo/storybook';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import { useAddSportProductNatures } from '../../hooks/mutations/useAddSportProductNatures';
import './architecture.scss';

type ProductNatureModalProps = {
  isOpen: boolean;
  sportId: number;
  setIsOpenModal: (value: boolean) => void;
  productNaturesToHide?: number[];
};

export const SportProductNaturesModal = ({
  isOpen,
  setIsOpenModal,
  sportId,
  productNaturesToHide = [],
}: ProductNatureModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [productNaturesOptions, setProductNaturesOptions] = useState<
    SelectOptions[]
  >([]);

  const queryClient = useQueryClient();

  const closeModal = () => {
    setIsOpenModal(false);
  };

  const mutationSuccess = () => {
    closeModal();
    setProductNaturesOptions([]);
    queryClient.invalidateQueries({
      queryKey: ['useNaturesArchitectureTable', sportId],
    });
    addToast(
      t('architecture.add-product.success'),
      'success',
      4500,
      'archi-add-product-nature-success',
    );
  };

  const mutationError = () => {
    closeModal();
    addToast(
      t('architecture.add-product.error'),
      'danger',
      4500,
      'archi-add-product-nature-error',
    );
  };

  const { mutate } = useAddSportProductNatures(
    sportId,
    productNaturesOptions.map((pn) => pn.value),
    mutationSuccess,
    mutationError,
  );

  const footerModal = (
    <>
      <VpButton
        data-testid="architecture_product-natures_modal_cancel-button"
        variant="secondary"
        shape="squared"
        size="medium"
        onClick={closeModal}
      >
        {t('cancel')}
      </VpButton>
      <VpButton
        data-testid="architecture_product-natures_modal_action-button"
        variant="primary"
        shape="squared"
        size="medium"
        onClick={() => mutate()}
        disabled={productNaturesOptions.length === 0}
      >
        {t('architecture.sport-product-natures-modal.add')}
      </VpButton>
    </>
  );

  return (
    <Modal
      dataTestid="architecture_product-natures_modal"
      footer={footerModal}
      isOpen={isOpen}
      title={t('architecture.sport-product-natures-modal.title')}
      ariaLabel="Sport Product Natures Modal"
      onClose={closeModal}
    >
      <div className="modal-architecture-body">
        <div className="architecture-modal-subtitle">
          {t('architecture.sport-product-natures-modal.subtitle')}
        </div>
        <ProductNaturesSearchInput
          onChange={setProductNaturesOptions}
          selectedProductNatureOptions={productNaturesOptions}
          productNaturesToHide={productNaturesToHide}
          sportIds={[sportId]}
          referential
        />
      </div>
    </Modal>
  );
};
